/**
 * [TD-47.6c-D.2] PlayerAnalysisCanvas - Canvas v2 layout per Spec §5.1.
 *
 * Mounted inside `PlayerAnalysisModal` when `analyticsEnabled && pgfCanvas`
 * (alpha flag fallback rule: when the canvas data is absent we render the
 * legacy 3-column modal layout instead - see PlayerAnalysisModal).
 *
 * Spec §5.1 vertical-band layout:
 *   Band 1 - Bye Week strip (header lives in the modal shell)
 *   Band 2 - PGF Rank · Tier · PGF Value · Default Positional Ranking
 *   Band 3 - Radar (left) | Position-specific primary stats (middle) | Gold/Silver socket (right)
 *   Band 4 - Weekly Matchup Bar (Weeks 1-18)
 *   Band 5 - PGF Analysis paragraph + collapsible deep-dive (secondary metrics)
 *
 * Per Analytics Narrative Discipline Guard (A.7): the paragraph eyebrow
 * reads "PGF Analysis" - see Build Contract Section A.7 for the
 * disallowed phrase list. The Gold/Silver socket (Band 3 right column)
 * is reserved for D.3 - for now it renders an empty placeholder.
 */
import { PGFRadarChart } from "./PGFRadarChart";
import { WeeklyMatchupBar } from "./WeeklyMatchupBar";
import { GoldSilverRibbon } from "./GoldSilverRibbon";
import { AdpDisplay, UnlistedPill } from "./AdpDisplay";

const TIER_DISPLAY = {
  elite: "Elite",
  top_tier: "Top-Tier",
  solid: "Solid",
  roster_worthy: "Roster-Worthy",
  bench: "Bench",
};

function tierLabel(raw) {
  if (!raw) return "";
  return TIER_DISPLAY[raw] || raw.replace(/_/g, " ");
}

export function PlayerAnalysisCanvas({
  player,
  pgfCanvas,
  analysisData,
  loading,
  error,
  isDark = true,
  showPgfValue = true,
}) {
  const titleColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const subColor = isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.62)";
  const cardBg = isDark ? "#0F0F12" : "#F4F4F4";
  const cardBorder = isDark ? "#2E2E2E" : "#D8D8D8";
  const accent = "#FF6B00";

  const rank = pgfCanvas?.pgf_rank || {};
  const value = pgfCanvas?.pgf_value;
  const axes = pgfCanvas?.axes;
  const weekly = pgfCanvas?.weekly_matchup_vector;
  const byeWeek = pgfCanvas?.player?.bye_week ?? player?.bye_week ?? null;
  const adp = analysisData?.adp;
  const proj = analysisData?.projected_points;
  // [WS-2 Part 3] Layer 3 reasoning note - natural-prose "sharp analyst
  // take" surfaced above the consensus paragraph. Empty string = silence
  // is valid; the block simply doesn't render.
  const reasoningNote = (pgfCanvas?.reasoning_note || "").trim();

  // [A1.4 Analytics Fixes Part A] Player photo for the modal header -
  // full-size Sleeper CDN variant. The player_id we're keyed on is the
  // PGF UUID; the Sleeper ID lives in provider_player_map and is
  // surfaced on the canvas payload as ``photo_url`` when the backend
  // has resolved it. Fallback: silhouette SVG (existing PlayerHeadshot).
  const photoUrl = pgfCanvas?.photo_url || player?.photo_url || null;

  // Position-specific primary stat label (footprint only - actual primary
  // stat values per-position depend on additional backend work; surfaced
  // in the deep-dive when available).
  const primaryStatHint = {
    QB: "Pass Yds / TDs",
    RB: "Carries · Targets",
    WR: "Target Share",
    TE: "Target Share",
  }[(player?.position || "").toUpperCase()] || "Primary stat";

  // [KG-12] Loading + error + insufficient-data early returns.
  // These land in the search overlay path and any future caller that
  // wires the fetch outside the component; the modal path preserves
  // its legacy layout fallback when pgfCanvas is absent.
  if (loading) {
    return (
      <div
        data-testid="player-analysis-canvas-loading"
        className="p-8 text-center text-sm"
        style={{ color: subColor }}
      >
        Loading analytics…
      </div>
    );
  }
  if (error) {
    return (
      <div
        data-testid="player-analysis-canvas-error"
        className="p-8 text-center text-sm"
        style={{ color: subColor }}
      >
        {error}
      </div>
    );
  }
  const insufficientData = Boolean(rank?.insufficient_data);

  return (
    <div data-testid="player-analysis-canvas" className="space-y-4">
      {/* ── BAND 1 - Player photo + name/team header ─────────────── */}
      <div
        data-testid="canvas-band-header"
        className="flex items-center gap-3"
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            data-testid="canvas-player-photo"
            className="w-16 h-16 rounded-full object-cover border"
            style={{ borderColor: cardBorder, background: cardBg }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div
            data-testid="canvas-player-photo-silhouette"
            className="w-16 h-16 rounded-full border flex items-center justify-center"
            style={{ borderColor: cardBorder, background: cardBg }}
          >
            <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke={subColor} strokeWidth="1.4">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
            </svg>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div
            className="font-display font-black text-lg leading-tight truncate"
            style={{ color: titleColor }}
            data-testid="canvas-player-name"
          >
            {player?.name || pgfCanvas?.player?.name || "-"}
          </div>
          <div
            className="font-body text-xs flex items-center gap-2"
            style={{ color: subColor }}
          >
            <span data-testid="canvas-player-pos-team">
              {(player?.position || "").toUpperCase()}
              {player?.nfl_team ? ` · ${player.nfl_team}` : ""}
            </span>
            <span aria-hidden style={{ opacity: 0.4 }}>·</span>
            <span
              className="font-display font-bold uppercase tracking-wider"
              style={{ color: accent }}
            >
              Bye
            </span>
            <span style={{ color: titleColor }} data-testid="canvas-bye-week">
              {byeWeek != null ? byeWeek : "-"}
            </span>
          </div>
        </div>
      </div>

      {/* ── BAND 2 - Rank · Value · Default ──────────────────────── */}
      <div
        data-testid="canvas-band-rank-value"
        className="flex flex-wrap items-baseline justify-between gap-3 rounded-lg px-4 py-3"
        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
      >
        <div className="flex flex-wrap items-baseline gap-3">
          <span
            className="font-display font-black text-[11px] uppercase tracking-widest"
            style={{ color: accent }}
          >
            PGF Rank
          </span>
          <span
            className="font-display font-bold text-2xl leading-none"
            style={{ color: titleColor }}
            data-testid="canvas-pgf-rank-label"
          >
            {rank.positional_label || "-"}
            <span className="text-base ml-2" style={{ color: subColor }}>
              · {rank.score ?? "-"}
            </span>
          </span>
          <span
            className="font-body text-xs uppercase tracking-wide"
            style={{ color: insufficientData ? "#F59E0B" : subColor }}
            data-testid="canvas-pgf-tier"
          >
            {insufficientData ? "Insufficient Data" : tierLabel(rank.tier_label)}
          </span>
        </div>

        <div className="flex flex-wrap items-baseline gap-3">
          {/* [A3 Analytics Fixes Part A] PGF Value is auction dollars -
              only meaningful inside a league's budget/settings. Rendered
              here only when the caller supplies a value (draft-room
              context). On the league-agnostic Players view the caller
              does not pass a value and the block is suppressed entirely
              - no placeholder text. Snake callers pass
              showPgfValue={false}: there are no dollars in a snake draft,
              so the figure is noise there (founder call). */}
          {showPgfValue && value ? (
            <span
              className="font-display font-bold text-xl"
              style={{ color: titleColor }}
              data-testid="canvas-pgf-value"
            >
              ${value.dollars}
              <span className="font-body text-xs ml-1.5" style={{ color: subColor }}>
                (${value.range_low}-${value.range_high})
              </span>
            </span>
          ) : null}
          {adp != null ? (
            <span
              className="font-body text-xs"
              style={{ color: subColor }}
              data-testid="canvas-default-ranking"
            >
              Default: ADP {adp.toFixed(1)}
            </span>
          ) : (
            <span
              className="font-body text-xs inline-flex items-center gap-1.5"
              style={{ color: subColor }}
              data-testid="canvas-default-ranking-unlisted"
            >
              Default: <UnlistedPill />
            </span>
          )}
        </div>
      </div>

      {/* ── BAND 3 - Radar | Primary stats | Gold/Silver socket ─── */}
      <div
        data-testid="canvas-band-radar-grid"
        className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-stretch"
      >
        <div className="sm:col-span-5">
          <PGFRadarChart axes={axes} />
        </div>

        <div
          className="sm:col-span-4 rounded-lg px-3 py-2"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
          data-testid="canvas-primary-stats"
        >
          <div
            className="font-display text-[10px] uppercase tracking-[0.22em] mb-2"
            style={{ color: subColor }}
          >
            Primary stats
          </div>
          <div className="space-y-1.5">
            <StatRow
              label="Projected"
              value={proj != null ? `${proj.toFixed(0)} pts` : "-"}
              titleColor={titleColor}
              subColor={subColor}
            />
            <StatRow
              label="ADP"
              value={adp != null ? adp.toFixed(1) : <UnlistedPill />}
              titleColor={titleColor}
              subColor={subColor}
            />
            <StatRow
              label="Bye"
              value={byeWeek != null ? `Wk ${byeWeek}` : "-"}
              titleColor={titleColor}
              subColor={subColor}
            />
            <StatRow
              label={primaryStatHint}
              value={<span className="italic" style={{ color: subColor }}>see deep-dive</span>}
              titleColor={titleColor}
              subColor={subColor}
            />
          </div>
        </div>

        {/* [TD-47.6c-D.3] Gold/Silver alert ribbon - fills the socket
            reserved in D.2. Renders gold/silver/none based on the
            backend-derived alert payload from the canvas endpoint. */}
        <div
          className="sm:col-span-3 rounded-lg px-2 py-2"
          style={{
            background: cardBg,
            border: `1px solid ${cardBorder}`,
          }}
          data-testid="canvas-gold-silver-socket"
        >
          <GoldSilverRibbon alert={pgfCanvas?.gold_silver_alert} isDark={isDark} />
        </div>
      </div>

      {/* ── BAND 4 - Weekly Matchup Bar ──────────────────────────── */}
      <div data-testid="canvas-band-weekly">
        <div
          className="font-display text-[10px] uppercase tracking-[0.22em] mb-1.5"
          style={{ color: subColor }}
        >
          Weekly matchup
        </div>
        <WeeklyMatchupBar weeklyVector={weekly} byeWeek={byeWeek} />
      </div>

      {/* ── BAND 5 - PGF Analysis paragraph + deep-dive ───────────── */}
      <div data-testid="canvas-band-analysis" className="space-y-2">
        <div
          className="font-display text-[10px] uppercase tracking-[0.22em]"
          style={{ color: accent }}
          data-testid="canvas-analysis-eyebrow"
        >
          PGF Analysis
        </div>
        {/* [WS-2 Part 3] Reasoning-note prose caption - surfaces only when
            genuinely notable. Rendered as an italic pull-quote above the
            longer consensus paragraph so it reads like the sharp
            analyst's one-line verdict. */}
        {reasoningNote ? (
          <p
            className="font-body text-[13px] leading-relaxed italic"
            style={{
              color: titleColor,
              borderLeft: `2px solid ${accent}`,
              paddingLeft: "10px",
            }}
            data-testid="canvas-reasoning-note"
          >
            {reasoningNote}
          </p>
        ) : null}
        {loading ? (
          <p
            className="font-body text-sm italic"
            style={{ color: subColor }}
            data-testid="canvas-analysis-loading"
          >
            Pulling the data…
          </p>
        ) : error ? (
          <p
            className="font-body text-sm leading-relaxed"
            style={{ color: titleColor }}
            data-testid={
              error.kind === "auth"
                ? "canvas-analysis-auth"
                : "canvas-analysis-fallback"
            }
          >
            {error.text}
          </p>
        ) : (
          <p
            className="font-body text-sm leading-relaxed"
            style={{ color: titleColor }}
            data-testid="canvas-analysis-paragraph"
          >
            {analysisData?.consensus || "-"}
          </p>
        )}

        <button
          type="button"
          data-testid="canvas-deep-dive-header"
          className="font-display font-bold text-[11px] uppercase tracking-wider flex items-center gap-1.5"
          style={{ color: accent, cursor: "default" }}
          aria-expanded="true"
          disabled
        >
          PGF Deep-Dive
        </button>

        {/* [A1.2/A1.3 Analytics Fixes Part A] Deep-dive is now ALWAYS
            visible - no collapse. The redundant per-axis score grid
            (which just repeated the radar chart directly above) has
            been removed. This container is structurally prepared for
            the three-persona analysis (Coach / Scout / Doctor)
            landing in Part B; until then a lightweight loading state
            reads honestly rather than reprinting axis numbers. */}
        <div
          data-testid="canvas-deep-dive-body"
          className="rounded-lg px-3 py-3 mt-2"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          <p
            className="font-body text-[12px] italic"
            style={{ color: subColor }}
            data-testid="canvas-deep-dive-placeholder"
          >
            Scouting analysis loading - the Coach, Scout, and Doctor
            perspectives land in the next release.
          </p>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value, titleColor, subColor }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="font-body text-[11px] uppercase tracking-wider" style={{ color: subColor }}>
        {label}
      </span>
      <span className="font-display font-bold text-sm" style={{ color: titleColor }}>
        {value}
      </span>
    </div>
  );
}

export default PlayerAnalysisCanvas;
